/**
 * Kinds of reminders about open working days and open project attributions (backend `ReminderKind`),
 * and the notice on remaining leave (`LeaveNotice`, T-164, backend `LeaveNoticePush`).
 */
export type ReminderKind =
  | "StillClockedIn"
  | "DayNotClosed"
  | "DeadlineEscalation"
  | "ProjectAttributionOpen"
  | "ProjectAttributionOverdue"
  | "LeaveNotice";

/** Kinds of absence notifications (T-167, backend `AbsenceNotificationJob`). */
export type AbsenceNotificationKind =
  | "AbsenceRequested"
  | "AbsenceCancellationRequested"
  | "AbsenceApproved"
  | "AbsenceRejected"
  | "AbsenceCancellationApproved"
  | "AbsenceCancellationRejected"
  | "AbsenceRecorded"
  | "AbsenceCancelled";

/** Kinds of roster notifications (T-178, requests about shifts T-179, backend `RosterNotificationJob`). */
export type RosterNotificationKind =
  | "RosterPublished"
  | "RosterChanged"
  | "RosterShiftRequestReceived"
  | "RosterShiftRequestToApprove"
  | "RosterShiftRequestUpdated";

/** Kind of a budget warning to the persons responsible for a project (T-185, `ProjectBudgetAlertJob`). */
export type ProjectBudgetNotificationKind = "ProjectBudgetThresholdReached";

/** Audiences of reminders (backend `ReminderAudience`). */
export type ReminderAudience = "Employee" | "Manager" | "HrAdmin";

/**
 * Content of a reminder push message (backend `PushMessage`). It carries no names and no
 * working times; the text is rendered here.
 */
export interface ReminderPayload {
  readonly type: "reminder" | "absence" | "roster" | "projectBudget";
  readonly kind:
    ReminderKind | AbsenceNotificationKind | RosterNotificationKind | ProjectBudgetNotificationKind;
  readonly audience: ReminderAudience;
  /** Local date of the open day (ISO 8601). */
  readonly workDate: string;
  /** Application path opened by the notification. */
  readonly url: string;
  /** Replaces an earlier notification about the same subject. */
  readonly tag: string;
}

/** An action button of a notification. */
export interface NotificationActionContent {
  readonly action: string;
  readonly title: string;
}

/** Title and options passed to `showNotification`. */
export interface NotificationContent {
  readonly title: string;
  readonly options: {
    readonly body: string;
    readonly tag: string;
    readonly lang: string;
    readonly icon: string;
    readonly badge: string;
    readonly data: { readonly url: string };
    readonly actions: readonly NotificationActionContent[];
  };
}

/** Languages of notification texts. */
export type NotificationLanguage = "de" | "en";

/** Action that opens the clock screen to clock out (spec 11.4). */
export const clockOutAction = "clock-out";

const kinds: readonly string[] = [
  "StillClockedIn",
  "DayNotClosed",
  "DeadlineEscalation",
  "ProjectAttributionOpen",
  "ProjectAttributionOverdue",
  "LeaveNotice",
];

/**
 * Kinds with a text of their own per audience that never offer clocking out: project attributions
 * (T-122) and the notice on remaining leave (T-164).
 */
const projectKinds: readonly string[] = [
  "ProjectAttributionOpen",
  "ProjectAttributionOverdue",
  "LeaveNotice",
];

/** Kinds of roster notifications (T-178, T-179); they name no time, shift or person. */
const rosterKinds: readonly string[] = [
  "RosterPublished",
  "RosterChanged",
  "RosterShiftRequestReceived",
  "RosterShiftRequestToApprove",
  "RosterShiftRequestUpdated",
];

/** Kinds of budget warnings (T-185); they name no project, hours or person. */
const projectBudgetKinds: readonly string[] = ["ProjectBudgetThresholdReached"];

/** Kinds of absence notifications (T-167); they name no person, kind of absence or period. */
const absenceKinds: readonly string[] = [
  "AbsenceRequested",
  "AbsenceCancellationRequested",
  "AbsenceApproved",
  "AbsenceRejected",
  "AbsenceCancellationApproved",
  "AbsenceCancellationRejected",
  "AbsenceRecorded",
  "AbsenceCancelled",
];
const audiences: readonly string[] = ["Employee", "Manager", "HrAdmin"];
const icon = "/icons/icon-192.png";
// Monochrome silhouette of the mark; Android shows the badge as a mask in the status bar.
const badge = "/icons/badge-96.png";

interface Texts {
  readonly title: string;
  readonly body: string;
}

const texts: Readonly<Record<NotificationLanguage, Readonly<Record<string, Texts>>>> = {
  de: {
    "Employee:StillClockedIn": {
      title: "Sie sind noch eingestempelt",
      body: "Arbeitstag {date}: Bitte stempeln Sie aus, wenn Sie nicht mehr arbeiten.",
    },
    "Employee:DayNotClosed": {
      title: "Arbeitstag nicht abgeschlossen",
      body: "Für den {date} fehlt das Arbeitsende. Bitte stempeln Sie aus oder stellen Sie einen Korrekturantrag.",
    },
    Manager: {
      title: "Offener Arbeitstag im Team",
      body: "Ein Arbeitstag vom {date} ist noch nicht abgeschlossen.",
    },
    HrAdmin: {
      title: "Arbeitstag weiterhin offen",
      body: "Ein Arbeitstag vom {date} ist noch offen. Für manche Beschäftigte gelten gesetzliche Aufzeichnungsfristen, z. B. sieben Tage nach § 17 MiLoG.",
    },
    "Employee:ProjectAttributionOpen": {
      title: "Projektzeiten zuordnen",
      body: "Am {date} ist noch Arbeitszeit keinem Projekt zugeordnet.",
    },
    "Manager:ProjectAttributionOverdue": {
      title: "Offene Projektzuordnung im Team",
      body: "Für einen Arbeitstag vom {date} fehlt nach Ablauf der Frist die Projektzuordnung.",
    },
    "HrAdmin:ProjectAttributionOverdue": {
      title: "Offene Projektzuordnung",
      body: "Für einen Arbeitstag vom {date} fehlt nach Ablauf der Frist die Projektzuordnung.",
    },
    "Employee:LeaveNotice": {
      title: "Hinweis zu Ihrem Resturlaub",
      body: "Hinweis vom {date}: Bitte lesen und bestätigen Sie ihn in der App unter „Abwesenheiten“.",
    },
    "Manager:AbsenceRequested": {
      title: "Neuer Abwesenheitsantrag",
      body: "Ein Abwesenheitsantrag aus Ihrem Team wartet auf Ihre Entscheidung.",
    },
    "HrAdmin:AbsenceRequested": {
      title: "Neuer Abwesenheitsantrag",
      body: "Ein Abwesenheitsantrag wartet auf die Entscheidung der Personalabteilung.",
    },
    "Manager:AbsenceCancellationRequested": {
      title: "Stornierung beantragt",
      body: "Für eine genehmigte Abwesenheit aus Ihrem Team wurde die Stornierung beantragt.",
    },
    "HrAdmin:AbsenceCancellationRequested": {
      title: "Stornierung beantragt",
      body: "Für eine genehmigte Abwesenheit wurde die Stornierung beantragt.",
    },
    "Employee:AbsenceApproved": {
      title: "Antrag genehmigt",
      body: "Ihr Abwesenheitsantrag wurde genehmigt.",
    },
    "Employee:AbsenceRejected": {
      title: "Antrag abgelehnt",
      body: "Ihr Abwesenheitsantrag wurde abgelehnt; die Begründung steht in der App.",
    },
    "Employee:AbsenceCancellationApproved": {
      title: "Stornierung genehmigt",
      body: "Die Stornierung Ihrer Abwesenheit wurde genehmigt.",
    },
    "Employee:AbsenceCancellationRejected": {
      title: "Stornierung abgelehnt",
      body: "Die Stornierung Ihrer Abwesenheit wurde abgelehnt; die Begründung steht in der App.",
    },
    "Employee:AbsenceRecorded": {
      title: "Abwesenheit eingetragen",
      body: "Für Sie wurde eine Abwesenheit eingetragen.",
    },
    "Employee:AbsenceCancelled": {
      title: "Abwesenheit storniert",
      body: "Eine Abwesenheit von Ihnen wurde storniert.",
    },
    "Employee:RosterPublished": {
      title: "Dienstplan veröffentlicht",
      body: "Ein Dienstplan mit Schichten von Ihnen wurde veröffentlicht.",
    },
    "Employee:RosterChanged": {
      title: "Dienstplan geändert",
      body: "Schichten von Ihnen haben sich geändert.",
    },
    "Employee:RosterShiftRequestReceived": {
      title: "Anfrage zu einer Schicht",
      body: "Eine Kollegin oder ein Kollege bittet Sie, eine Schicht zu übernehmen.",
    },
    "Employee:RosterShiftRequestToApprove": {
      title: "Schichtanfrage zur Freigabe",
      body: "Eine Anfrage zu einer Schicht wartet auf Ihre Entscheidung.",
    },
    "Employee:RosterShiftRequestUpdated": {
      title: "Schichtanfrage geändert",
      body: "Eine Anfrage zu einer Schicht, an der Sie beteiligt sind, hat einen neuen Stand.",
    },
    "Manager:ProjectBudgetThresholdReached": {
      title: "Projektbudget",
      body: "Ein Budget eines Projekts, für das Sie verantwortlich sind, hat eine Warnschwelle erreicht.",
    },
    fallback: { title: "Zeitavo", body: "Es gibt eine neue Nachricht." },
    action: { title: "Jetzt ausstempeln", body: "" },
  },
  en: {
    "Employee:StillClockedIn": {
      title: "You are still clocked in",
      body: "Working day {date}: please clock out if you are no longer working.",
    },
    "Employee:DayNotClosed": {
      title: "Working day not closed",
      body: "The end of work on {date} is missing. Please clock out or request a correction.",
    },
    Manager: {
      title: "Open working day in your team",
      body: "A working day on {date} is not closed yet.",
    },
    HrAdmin: {
      title: "Working day still open",
      body: "A working day on {date} is still open. Statutory recording deadlines apply to some employees, for example seven days under § 17 MiLoG.",
    },
    "Employee:ProjectAttributionOpen": {
      title: "Attribute project time",
      body: "Working time on {date} is not attributed to a project yet.",
    },
    "Manager:ProjectAttributionOverdue": {
      title: "Open project attribution in your team",
      body: "A working day on {date} still lacks project attributions after the deadline.",
    },
    "HrAdmin:ProjectAttributionOverdue": {
      title: "Open project attribution",
      body: "A working day on {date} still lacks project attributions after the deadline.",
    },
    "Employee:LeaveNotice": {
      title: "Notice on your remaining leave",
      body: "Notice of {date}: please read and confirm it in the app under “Absences”.",
    },
    "Manager:AbsenceRequested": {
      title: "New absence request",
      body: "An absence request of your team is waiting for your decision.",
    },
    "HrAdmin:AbsenceRequested": {
      title: "New absence request",
      body: "An absence request is waiting for the decision of HR.",
    },
    "Manager:AbsenceCancellationRequested": {
      title: "Cancellation requested",
      body: "The cancellation of an approved absence of your team was requested.",
    },
    "HrAdmin:AbsenceCancellationRequested": {
      title: "Cancellation requested",
      body: "The cancellation of an approved absence was requested.",
    },
    "Employee:AbsenceApproved": {
      title: "Request approved",
      body: "Your absence request was approved.",
    },
    "Employee:AbsenceRejected": {
      title: "Request rejected",
      body: "Your absence request was rejected; the reason is shown in the app.",
    },
    "Employee:AbsenceCancellationApproved": {
      title: "Cancellation approved",
      body: "The cancellation of your absence was approved.",
    },
    "Employee:AbsenceCancellationRejected": {
      title: "Cancellation rejected",
      body: "The cancellation of your absence was rejected; the reason is shown in the app.",
    },
    "Employee:AbsenceRecorded": {
      title: "Absence recorded",
      body: "An absence was recorded for you.",
    },
    "Employee:AbsenceCancelled": {
      title: "Absence cancelled",
      body: "An absence of yours was cancelled.",
    },
    "Employee:RosterPublished": {
      title: "Roster published",
      body: "A roster with shifts of yours was published.",
    },
    "Employee:RosterChanged": {
      title: "Roster changed",
      body: "Shifts of yours have changed.",
    },
    "Employee:RosterShiftRequestReceived": {
      title: "Request about a shift",
      body: "A colleague asks you to take over a shift.",
    },
    "Employee:RosterShiftRequestToApprove": {
      title: "Shift request to approve",
      body: "A request about a shift awaits your decision.",
    },
    "Employee:RosterShiftRequestUpdated": {
      title: "Shift request updated",
      body: "A request about a shift you take part in has a new state.",
    },
    "Manager:ProjectBudgetThresholdReached": {
      title: "Project budget",
      body: "A budget of a project you are responsible for reached a warning threshold.",
    },
    fallback: { title: "Zeitavo", body: "There is a new message." },
    action: { title: "Clock out now", body: "" },
  },
};

/**
 * Checks that a path is an internal application path (no scheme, no host, no protocol-relative
 * URL), so that a notification can never open a foreign site.
 *
 * @param path - The candidate path.
 * @returns `true` for an internal path.
 */
export function isInternalPath(path: string): boolean {
  const internal = path.startsWith("/") && !path.startsWith("//") && !path.includes("\\");

  return internal;
}

/**
 * Validates the data of a push message.
 *
 * @param data - Parsed JSON of the message, or `null` if it had no valid JSON.
 * @returns The reminder, or `null` for unknown content.
 */
export function parsePushPayload(data: unknown): ReminderPayload | null {
  if (typeof data !== "object" || data === null) {
    return null;
  }

  const record = data as Readonly<Record<string, unknown>>;
  const { type, kind, audience, workDate, url, tag } = record;
  const valid =
    ((type === "reminder" && typeof kind === "string" && kinds.includes(kind)) ||
      (type === "absence" && typeof kind === "string" && absenceKinds.includes(kind)) ||
      (type === "roster" && typeof kind === "string" && rosterKinds.includes(kind)) ||
      (type === "projectBudget" &&
        typeof kind === "string" &&
        projectBudgetKinds.includes(kind))) &&
    typeof audience === "string" &&
    audiences.includes(audience) &&
    typeof workDate === "string" &&
    /^\d{4}-\d{2}-\d{2}$/u.test(workDate) &&
    typeof url === "string" &&
    isInternalPath(url) &&
    typeof tag === "string" &&
    tag !== "";
  const payload = valid ? (data as ReminderPayload) : null;

  return payload;
}

/**
 * Formats an ISO date for the notification text.
 *
 * @param isoDate - Date in the form `yyyy-mm-dd`.
 * @param language - Language.
 * @returns The formatted date.
 */
function formatDate(isoDate: string, language: NotificationLanguage): string {
  const [year, month, day] = isoDate.split("-");
  const formatted = language === "de" ? `${day ?? ""}.${month ?? ""}.${year ?? ""}` : isoDate;

  return formatted;
}

/**
 * Builds the notification for a push message. Unknown content still produces a neutral
 * notification, because browsers require a visible notification for every push message.
 *
 * @param payload - The validated reminder, or `null`.
 * @param language - Language of the texts.
 * @returns Title and options.
 */
export function buildNotification(
  payload: ReminderPayload | null,
  language: NotificationLanguage,
): NotificationContent {
  const dictionary = texts[language];
  const aboutProjects = payload !== null && projectKinds.includes(payload.kind);
  // Absence, roster and budget notifications (T-167, T-178, T-185) have their own texts per kind and
  // no date.
  const aboutAbsences =
    payload !== null &&
    (payload.type === "absence" || payload.type === "roster" || payload.type === "projectBudget");
  const key =
    payload === null
      ? "fallback"
      : aboutProjects || aboutAbsences
        ? `${payload.audience}:${payload.kind}`
        : payload.audience === "Employee"
          ? `Employee:${payload.kind === "StillClockedIn" ? "StillClockedIn" : "DayNotClosed"}`
          : payload.audience;
  const selected = dictionary[key] ?? dictionary["fallback"] ?? { title: "", body: "" };
  const date = payload === null ? "" : formatDate(payload.workDate, language);
  const offersClockOut = payload?.audience === "Employee" && !aboutProjects && !aboutAbsences;
  const content: NotificationContent = {
    title: selected.title,
    options: {
      body: selected.body.replace("{date}", date),
      tag: payload?.tag ?? "timetracking",
      lang: language,
      icon,
      badge,
      data: { url: payload?.url ?? "/" },
      actions: offersClockOut
        ? [{ action: clockOutAction, title: dictionary["action"]?.title ?? "" }]
        : [],
    },
  };

  return content;
}

/**
 * Returns the path a click on a notification opens. The clock-out action always opens the clock
 * screen; nothing is recorded automatically.
 *
 * @param data - The `data` of the notification.
 * @param action - The clicked action, empty for the notification itself.
 * @returns An internal path.
 */
export function notificationTarget(data: unknown, action: string): string {
  if (action === clockOutAction) {
    return "/";
  }

  const url =
    typeof data === "object" && data !== null && "url" in data
      ? (data as { readonly url: unknown }).url
      : null;
  const target = typeof url === "string" && isInternalPath(url) ? url : "/";

  return target;
}

/**
 * Chooses the language of notification texts from the browser language.
 *
 * @param browserLanguage - `navigator.language`.
 * @returns German unless the browser prefers English.
 */
export function notificationLanguage(browserLanguage: string): NotificationLanguage {
  const language: NotificationLanguage = browserLanguage.toLowerCase().startsWith("en")
    ? "en"
    : "de";

  return language;
}
